import axios from 'axios';
import { API } from 'constants/global';
import { Stater } from 'helpers/utils';

export interface IApiResponse {
  message: string;
  success: boolean;
  data?: any;
}

export interface IHttpResponse<T = any> {
  data: T;
  status: number;
  hasError?: boolean;
  statusText?: string;
  message?: string;
}

const defaultResponseError: IHttpResponse = {
  data: null,
  status: 404,
};

const HttpClient = axios.create({
  baseURL: API.BASEPATH,
  timeout: 15000,
});

// Retry transient network / timeout failures (no server response) with
// exponential backoff. Real HTTP responses (4xx/5xx) are never retried.
// The app's write endpoints are idempotent, so retrying POST/PATCH is safe.
const RETRY_MAX = 2;
const RETRY_BASE_DELAY_MS = 800;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Interceptor
HttpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config: any = error?.config;

    if (config && !error.response) {
      const retryCount: number = config.__retryCount ?? 0;
      if (retryCount < RETRY_MAX) {
        config.__retryCount = retryCount + 1;
        await wait(Math.min(RETRY_BASE_DELAY_MS * 2 ** retryCount, 4000));
        return HttpClient(config);
      }
    }

    const result: IHttpResponse = {
      ...defaultResponseError,
      ...(error.status ? { status: error.status } : {}),
      hasError: true,
      message: error.message,
    };

    // Error
    if (error.response) {
      result.data = error.response.data;
      result.status = error.response.status || result.status;
      if (result.data) {
        result.data.message && (result.statusText = result.data.message);
        result.data.errors &&
          result.data.errors.length &&
          (result.statusText = result.data.errors.join('<br />'));
      }
    }

    return Promise.reject(result);
  },
);

// Loading throttler
const ION_LOADING_STATE = new Stater();

export { HttpClient, Stater, ION_LOADING_STATE };
